import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { TextField } from '@app/components/form-fields/TextField';
import { SelectField } from '@app/components/form-fields/SelectField';
import type { Location } from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ModalProps } from 'antd';
import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import {
    Controller,
    // useFieldArray,
    useForm,
} from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';
import useBrands from '@app/hooks/brands/useBrands';
import { countryOptions } from '@app/utils/constants';

const schema = z.object({
    name: z.string().trim().min(1, 'Location name is required'),
    brand_id: z.string('Brand is required'),

    // Contact
    contact_name: z.string().trim().min(1, 'Admin name is required'),
    contact_email: z
        .string()
        .email('Admin email must be valid')
        .trim()
        .min(1, 'Admin email is required'),
    contact_telephone: z.string().trim().min(1, 'Admin phone is required'),

    // Address
    address_line1: z.string().trim().min(1, 'Address Line 1 is required'),
    address_line2: z.string().trim(),
    city: z.string().trim().min(1, 'City is required'),
    post_code: z.string().trim().min(1, 'Post code is required'),
    country: z.string().trim().min(1, 'Country is required'),

    // CRM
    // crmCredentials: z
    //     .array(
    //         z.object({
    //             key: z.string().trim().min(1, 'Key is required'),
    //             value: z.string().trim().min(1, 'Value is required'),
    //         })
    //     )
    //     .optional(),

    // postBookingsTo: z.enum(['crm', 'leadpro']),
});

export type LocationFormValues = z.infer<typeof schema>;

export type LocationFormProps = ModalProps & {
    id: string;
    data?: Location;
    isEdit?: boolean;
    onSubmit: (
        data: LocationFormValues & { channel_id: string },
        reset: UseFormReset<LocationFormValues>
    ) => void;
    /**
     * Optional handler invoked when user clicks "Test Connection".
     * Receives current form values and may return a Promise<boolean> or void.
     */
    onTestConnection?: (values: LocationFormValues) => Promise<boolean> | void;
};

const DEFAULT_VALUES = {
    name: '',
    brand_id: undefined,
    contact_name: '',
    contact_email: '',
    contact_telephone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    post_code: '',
    country: 'GB',
    // crmCredentials: [],
    // postBookingsTo: 'crm',
};

export const LocationForm = ({
    onCancel,
    onSubmit,
    data,
    isEdit = false,
    //   onTestConnection,
    ...props
}: LocationFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        // getValues,
        formState: { isValid, isSubmitting },
    } = useForm<LocationFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: DEFAULT_VALUES,
    });

    const { data: brandsData } = useBrands({ per_page: 10000 });

    // field array for crmCredentials
    // const { fields, append, remove } = useFieldArray({
    //     control,
    //     name: 'crmCredentials' as const,
    // });

    useEffect(() => {
        // reset with incoming data if present, otherwise keep defaults
        if (data) {
            reset({
                // id: data.id ?? '',
                name: data.name ?? '',
                brand_id: data.brand_id ?? undefined,
                contact_name: data.contact_name ?? '',
                contact_email: data.contact_email ?? '',
                contact_telephone: data.contact_telephone ?? '',
                address_line1: data.address_line1 ?? '',
                address_line2: data.address_line2 ?? '',
                city: data.city ?? '',
                post_code: data.post_code ?? '',
                country: data.country ?? 'GB',
                // crmCredentials:
                //     (data as any).crmCredentials &&
                //     Array.isArray((data as any).crmCredentials)
                //         ? (data as any).crmCredentials
                //         : [],
                // postBookingsTo: (data as any).postBookingsTo ?? 'crm',
            });
        }
    }, [data, reset]);

    // const handleTestConnectionClick = async (e: React.MouseEvent) => {
    //     e.preventDefault();
    //     const values = getValues();
    //     if (onTestConnection) {
    //         try {
    //             await onTestConnection(values);
    //         } catch (err) {
    //             // Parent should handle errors/display; we log for dev visibility
    //             // eslint-disable-next-line no-console
    //             console.error('onTestConnection error', err);
    //         }
    //     } else {
    //         // fallback for debugging if parent doesn't provide handler
    //         // eslint-disable-next-line no-console
    //         console.log('Test Connection values:', values);
    //     }
    // };

    const handlePreSubmit = (values: LocationFormValues) => {
        const channelId = brandsData?.data?.find((b) => b.id === values.brand_id)?.channel_id || '';
        onSubmit({ ...values, channel_id: channelId }, reset);
    };

    return (
        <form onSubmit={handleSubmit((values) => handlePreSubmit(values))} id={props.id}>
            <div className="form-fields-wrapper">
                <div className="grid">
                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Location Name"
                                    type="text"
                                    placeholder="Enter location name"
                                />
                            )}
                        />
                    </div>
                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="brand_id"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Brand"
                                    placeholder="Select brand"
                                    options={
                                        brandsData?.data?.map((brand) => ({
                                            label: brand.name,
                                            value: brand.id,
                                        })) ?? []
                                    }
                                />
                            )}
                        />
                    </div>

                    {/* Contact */}
                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="contact_name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Contact Name"
                                    type="text"
                                    placeholder="Enter contact name"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-6 grid-md-6">
                        <Controller
                            control={control}
                            name="contact_email"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    disabled={isEdit}
                                    label="Contact Email"
                                    type="email"
                                    placeholder="Enter contact email"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="contact_telephone"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Contact Phone"
                                    type="tel"
                                    placeholder="Enter contact phone"
                                />
                            )}
                        />
                    </div>

                    {/* Address */}
                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="address_line1"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Address Line 1"
                                    type="text"
                                    placeholder="Enter address line 1"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="address_line2"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Address Line 2"
                                    type="text"
                                    placeholder="Enter address line 2 (optional)"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-6">
                        <Controller
                            control={control}
                            name="city"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="City"
                                    type="text"
                                    placeholder="Enter city"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-6">
                        <Controller
                            control={control}
                            name="post_code"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Post Code"
                                    type="text"
                                    placeholder="Enter post code"
                                />
                            )}
                        />
                    </div>

                    <div className="grid-xs-12 grid-s-12 grid-md-12">
                        <Controller
                            control={control}
                            name="country"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Country"
                                    placeholder="Select Country"
                                    options={countryOptions}
                                />
                            )}
                        />
                    </div>

                    {/* CRM Credentials (dynamic) */}
                    {/* <div className="grid-xs-12 grid-s-12 grid-md-12">
            <div style={{ marginBottom: 8 }}>CRM Credentials</div>
            {fields.map((f, idx) => (
              <div
                key={f.id}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Controller
                  control={control}
                  name={`crmCredentials.${idx}.key` as const}
                  render={({ field, fieldState }) => (
                    <TextField
                      fieldState={fieldState}
                      {...field}
                      label={idx === 0 ? "Key" : ""}
                      type="text"
                      placeholder="Key"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={`crmCredentials.${idx}.value` as const}
                  render={({ field, fieldState }) => (
                    <TextField
                      fieldState={fieldState}
                      {...field}
                      label={idx === 0 ? "Value" : ""}
                      type="text"
                      placeholder="Value"
                    />
                  )}
                />
                <StyledButton
                  onClick={() => remove(idx)}
                  color="black"
                  data-testid={`remove-crm-${idx}`}
                >
                  Remove
                </StyledButton>
              </div>
            ))}
            <StyledButton
              onClick={() => append({ key: "", value: "" })}
              color="black"
              data-testid="add-crm-credential"
            >
              + Add Field
            </StyledButton>
          </div> */}
                    {/* Post Bookings To (radio) + Test Connection */}
                    {/* <div
                        className='grid-xs-12 grid-s-12 grid-md-12'
                        style={{ marginTop: 12 }}
                    >
                        <div style={{ marginBottom: 8 }}>Post Bookings To</div>
                        <Controller
                            control={control}
                            name='postBookingsTo'
                            render={({ field }) => (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 16,
                                    }}
                                >
                                    <label
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <input
                                            type='radio'
                                            value='crm'
                                            checked={field.value === 'crm'}
                                            onChange={() =>
                                                field.onChange('crm')
                                            }
                                        />
                                        <span>CRM</span>
                                    </label>
                                    <label
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <input
                                            type='radio'
                                            value='leadpro'
                                            checked={field.value === 'leadpro'}
                                            onChange={() =>
                                                field.onChange('leadpro')
                                            }
                                        />
                                        <span>Leadpro</span>
                                    </label>
                                    <StyledButton
                                        onClick={handleTestConnectionClick}
                                        color='black'
                                        data-testid='test-connection-button'
                                    >
                                        Test Connection
                                    </StyledButton>
                                </div>
                            )}
                        />
                    </div> */}
                </div>
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton
                        onClick={(e) => onCancel?.(e as MouseEvent<HTMLButtonElement>)}
                        color="black"
                        data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
